import { truncateText } from '@ardoq/graph-labeling';
import { HORIZONTAL_ELLIPSIS } from '@ardoq/global-consts';

/** How wide a string is drawn. The view measures against a canvas; a test can be exact. */
export type MeasureLabel = (text: string) => number;

/** How a label is broken across lines. Rectangles hyphenate; circles and arcs do not. */
export type WrapLabel = (args: {
  text: string;
  availableWidth: number;
  measure: MeasureLabel;
  maxLines: number;
}) => string[];

type ToLeafLabelLinesArgs = {
  label: string;
  /** Already formatted and parenthesised, e.g. `(4.7M)`. */
  valueLabel: string;
  /** The tile's inner width, padding already taken off but not the icon. */
  availableWidth: number;
  /** The tile's inner height, padding already taken off. */
  availableHeight: number;
  measure: MeasureLabel;
  /** The height of one line at the size {@link measure} measures. */
  lineHeight: number;
  wrap: WrapLabel;
};

/**
 * A leaf shape's text, wrapped to the lines that fit and ending in its value.
 *
 * The value is what a treemap is read for, so it is reserved first and the name truncated around
 * it: letting the two share a budget lost the value off the bottom of every tile whose name was
 * long, which is how `SQL Server Reporting Services (SSRS)` came to be drawn with no number at
 * all. Where not even the value fits, the name goes entirely rather than both being clipped.
 */
export const toLeafText = ({
  label,
  valueLabel,
  availableWidth,
  availableHeight,
  measure,
  lineHeight,
  wrap,
}: ToLeafLabelLinesArgs): string[] => {
  const truncate = truncateText(measure);
  const whole = `${label} ${valueLabel}`;
  /* Before the resize observer has reported, and under a test renderer that lays nothing out,
     there is no width to measure against. The tile clips to nothing either way, so say what it
     is about rather than nothing at all. */
  if (availableWidth <= 0 || availableHeight <= 0) {
    return [whole];
  }
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

  /* What the value costs, which is not always one line: a tall narrow tile can hold
     `(28,5 tis. $)` as two, and rejecting it for not fitting on one left those tiles blank. */
  const valueLines = wrap({
    text: valueLabel,
    availableWidth,
    measure,
    maxLines,
  });
  if (valueLines.join(' ') !== valueLabel) {
    /* Not even the value fits, wrapped. Nothing legible does, and half a number reads as a
       different number — so draw nothing rather than the bare ellipsis this used to give. */
    return [];
  }

  const lines = wrap({ text: whole, availableWidth, measure, maxLines });
  if (lines.join(' ') === whole) {
    return lines;
  }

  /* Something was dropped, so lay the name out around the value instead. Where the value takes
     one line, the name shares it and keeps the front; where it takes more, it gets its own
     lines and the name takes what is above them. */
  const nameWidthOnLastLine = availableWidth - measure(` ${valueLabel}`);
  if (
    valueLines.length === 1 &&
    nameWidthOnLastLine > measure(HORIZONTAL_ELLIPSIS)
  ) {
    const nameLines = wrap({ text: label, availableWidth, measure, maxLines });
    const lastLine = nameLines.pop() ?? '';
    return [
      ...nameLines,
      `${truncate(lastLine, nameWidthOnLastLine)} ${valueLabel}`,
    ];
  }
  const linesForName = maxLines - valueLines.length;
  if (linesForName < 1) {
    return valueLines;
  }
  return [
    ...wrap({ text: label, availableWidth, measure, maxLines: linesForName }),
    ...valueLines,
  ];
};
